import type {Component, DeviceProperties, Editor} from 'grapesjs';
import {
  EMAIL_MAKER_PROPS,
  MOBILE_CSS_PROP_PREFIX,
  SECTION_RESPONSIVE_MOBILE_CLASS,
  SECTION_RESPONSIVE_TABLET_CLASS,
  SECTION_UNRESPONSIVE_CLASS,
  TABLET_CSS_PROP_PREFIX,
} from '@EmailMakerReact/email-maker-react.constants';
import {EmailMakerCanvasDevice} from '@EmailMakerReact/email-maker-react.types';
import {ResponsiveType} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {getBooleanProp} from '@EmailMakerReact/lib/email-maker-component-query-library';

/** Responsive / per-device CSS for the React email maker. */

/** `minWidthMedia` is a PMW custom device attribute set in grapesjs-email-maker-plugin/config/grapesjs.ts. */
type EmailMakerDeviceProperties = DeviceProperties & {
  minWidthMedia?: string;
};

export interface DeviceMediaQueryOptions {
  property: string;
  value: string;
  /** The ccid of the component the rule targets. */
  id: string;
  deviceID: EmailMakerCanvasDevice;
  cssPrefix: string;
}

export interface SectionResponsiveOptions {
  component?: Component;
  responsive: boolean;
  type: ResponsiveType;
}

/** Per-device responsive style prefix, keyed by device id. */
const RESPONSIVE_DEVICE_STYLE_PREFIX: Record<string, string> = {
  [EmailMakerCanvasDevice.MOBILE]: MOBILE_CSS_PROP_PREFIX,
  [EmailMakerCanvasDevice.TABLET]: TABLET_CSS_PROP_PREFIX,
};

/** Canonical ordered list of responsive devices (TABLET first, then MOBILE). */
const RESPONSIVE_DEVICES: readonly {deviceID: EmailMakerCanvasDevice; cssPrefix: string}[] = [
  {deviceID: EmailMakerCanvasDevice.TABLET, cssPrefix: TABLET_CSS_PROP_PREFIX},
  {deviceID: EmailMakerCanvasDevice.MOBILE, cssPrefix: MOBILE_CSS_PROP_PREFIX},
] as const;

/** Calls `cb` once per responsive device (TABLET, then MOBILE), passing each device's id and CSS-var prefix. */
export function forEachResponsiveDevice(cb: (deviceID: EmailMakerCanvasDevice, cssPrefix: string) => void): void {
  for (const {deviceID, cssPrefix} of RESPONSIVE_DEVICES) {
    cb(deviceID, cssPrefix);
  }
}

/** Writes a per-device `!important` CSS rule for the component id. */
export function setDeviceMediaQuery(editor: Editor, options: DeviceMediaQueryOptions): void {
  const device = editor.Devices.get(options.deviceID);
  if (!device) {
    return;
  }

  const maxWidth = device.getWidthMedia();
  // Boundary cast: the device was registered with the PMW custom minWidthMedia attribute (see config/grapesjs.ts).
  const minWidth = (device.attributes as EmailMakerDeviceProperties).minWidthMedia;
  const property = options.property.replace(options.cssPrefix, '');
  const atMediaRules = `(max-width: ${maxWidth})${minWidth ? ` and (min-width: ${minWidth})` : ''}`;

  editor.Css.setRule(
    `#${options.id}`,
    {
      [property]: `${options.value} !important`,
    },
    {
      atRuleType: 'media',
      atRuleParams: `screen and ${atMediaRules}`,
    }
  );
}

/** Re-syncs the component tree's `--mobile-*` / `--tablet-*` style props into per-device CSS rules. */
export function refreshMediaQueries(editor: Editor, component: Component | undefined, responsiveStyles: string[]): void {
  if (!component || responsiveStyles.length === 0) {
    return;
  }

  const componentStyles = component.getStyle();

  responsiveStyles.forEach((style) => {
    const mobileStyle = componentStyles[MOBILE_CSS_PROP_PREFIX + style];
    const tabletStyle = componentStyles[TABLET_CSS_PROP_PREFIX + style];

    if (typeof mobileStyle === 'string' && mobileStyle) {
      setDeviceMediaQuery(editor, {
        property: style,
        value: mobileStyle,
        id: component.ccid,
        deviceID: EmailMakerCanvasDevice.MOBILE,
        cssPrefix: MOBILE_CSS_PROP_PREFIX,
      });
    }

    if (typeof tabletStyle === 'string' && tabletStyle) {
      setDeviceMediaQuery(editor, {
        property: style,
        value: tabletStyle,
        id: component.ccid,
        deviceID: EmailMakerCanvasDevice.TABLET,
        cssPrefix: TABLET_CSS_PROP_PREFIX,
      });
    }
  });

  component.components().forEach((child) => {
    refreshMediaQueries(editor, child, responsiveStyles);
  });
}

/** Copies the component tree's per-device CSS rules back into `--mobile-*` / `--tablet-*` style props (run on load). */
export function copyMediaQueriesToResponsiveStyle(editor: Editor, component: Component | undefined, responsiveStyles: string[]): void {
  if (!component || responsiveStyles.length === 0) {
    return;
  }

  // Rules are read by getId() but written keyed by ccid; do not unify.
  const cssRules = editor.Css.getRules(`#${component.getId()}`);

  Object.keys(RESPONSIVE_DEVICE_STYLE_PREFIX).forEach((device) => {
    const deviceCssRules = cssRules.find((rule) => rule.getDevice()?.id === device);
    const deviceRuleStyles = deviceCssRules?.getStyle() ?? {};

    responsiveStyles.forEach((style) => {
      const ruleStyle = deviceRuleStyles[style];

      if (ruleStyle) {
        component.addStyle(`${RESPONSIVE_DEVICE_STYLE_PREFIX[device]}${style}`, ruleStyle);
      } else {
        component.removeStyle(`${RESPONSIVE_DEVICE_STYLE_PREFIX[device]}${style}`);
      }
    });
  });

  component.components().forEach((child) => {
    copyMediaQueriesToResponsiveStyle(editor, child, responsiveStyles);
  });
}

/** Toggles a section's mobile/tablet `responsive` model property (not an HTML attribute) and classes. */
export function updateSectionResponsive(editor: Editor, options: SectionResponsiveOptions): void {
  const component = options.component ?? editor.getSelected();
  if (!component) {
    return;
  }

  let responsiveClass: string = SECTION_RESPONSIVE_MOBILE_CLASS;
  let responsiveAttributeName: string = EMAIL_MAKER_PROPS.IS_RESPONSIVE_MOBILE;

  if (options.type === ResponsiveType.TABLET) {
    responsiveClass = SECTION_RESPONSIVE_TABLET_CLASS;
    responsiveAttributeName = EMAIL_MAKER_PROPS.IS_RESPONSIVE_TABLET;
  }

  component.set(responsiveAttributeName, options.responsive);
  if (options.responsive) {
    component.addClass(responsiveClass);
    component.removeClass(SECTION_UNRESPONSIVE_CLASS);
  } else {
    component.addClass(SECTION_UNRESPONSIVE_CLASS);
    component.removeClass(responsiveClass);
  }
}

/** Re-syncs both MOBILE and TABLET responsive types on a component from its stored IS_RESPONSIVE_MOBILE / IS_RESPONSIVE_TABLET props. */
export function updateAllSectionResponsive(editor: Editor, component: Component): void {
  updateSectionResponsive(editor, {
    component,
    responsive: getBooleanProp(component, EMAIL_MAKER_PROPS.IS_RESPONSIVE_MOBILE),
    type: ResponsiveType.MOBILE,
  });
  updateSectionResponsive(editor, {
    component,
    responsive: getBooleanProp(component, EMAIL_MAKER_PROPS.IS_RESPONSIVE_TABLET),
    type: ResponsiveType.TABLET,
  });
}
